import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { ErrorMessage, Field, Form, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { FaEye, FaPen } from "react-icons/fa";
import { config } from "../CommonUtils/CommonURLS";
import { CommonAxiosGet, CommonAxiosPost } from "../CommonUtils/CommonAxios";
import { ImagePdfBucket } from "../CommonUtils/ImagesBase64";
import { viewImage } from "../CommonUtils/viewImage";
import CommonCardHeader from "../CommonUtils/CommonCardHeader";
import CommonReactTable from "../CommonUtils/CommonReactTable";

const closeTicketSchema = Yup.object({
    remarks: Yup.string().required("Remarks are required").min(5, "Minimum 5 characters required"),
    // closeUpload: Yup.string().required("Supporting PDF is required")
});

function formatDateOnly(value) {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString("en-GB");
}

function CloseTicketModal({ isOpen, onClose, row, onSuccess }) {
    const userId = sessionStorage.getItem("user_id");

    const formik = useFormik({
        initialValues: {
            remarks: row?.status_remarks,
            closeUpload: ""
        },
        enableReinitialize: true,
        validationSchema: closeTicketSchema,
        onSubmit: async (values) => {
            const confirm = await Swal.fire({
                text: "Are you sure you want to close this ticket?",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, Submit & Close",
                cancelButtonText: "Cancel",
                backdrop: false
            });
            if (!confirm.isConfirmed) return;
            const payload = {
                sno: row?.sno,
                candidateId: row?.candidate_id,
                statusRemarks: values.remarks,
                supportDoc: values.closeUpload,
                status: "Closed",
                university: row?.univ_type,
            };

            try {
                const url = config.url.STU_URL + "update-grievance-status";
                const res = await CommonAxiosPost(url, payload);
                if (res?.data?.scode === "01") {
                    Swal.fire(res?.data?.sdesc || "Ticket closed successfully", "", "success");
                    if (typeof onSuccess === "function") onSuccess();
                    onClose();
                } else {
                    Swal.fire(res?.data?.sdesc || "Failed to close ticket", "", "error");
                }
            } catch (error) {
                Swal.fire("Unable to close ticket", "", "error");
            }
        }
    });

    if (!isOpen) return null;

    return (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.45)" }}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content border-0 shadow">
                    <div className="modal-header">
                        <h5 className="modal-title">Close Ticket</h5>
                        <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
                    </div>

                    <FormikProvider value={formik}>
                        <Form onSubmit={formik.handleSubmit}>
                            <div className="modal-body">
                                <div className="mb-2"><strong>Name:</strong> {row.applicant_name}</div>
                                <div className="mb-3"><strong>ID:</strong> {row.candidate_id}</div>

                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Remarks <span className="text-danger">*</span></label>
                                    <Field
                                        as="textarea"
                                        name="remarks"
                                        className="form-control"
                                        rows={4}
                                        placeholder="Enter remarks"
                                    />
                                    <ErrorMessage name="remarks" component="div" className="text-danger small mt-1" />
                                </div>

                                <div className="mb-2">
                                    <label className="form-label fw-semibold">Upload Supporting Document (PDF)</label>
                                    <div className="d-flex align-items-center gap-2">
                                        <input
                                            type="file"
                                            className="form-control"
                                            accept="application/pdf"
                                            onChange={(event) => {
                                                const fileName = `close_ticket_${row?.candidate_id || Date.now()}`;
                                                ImagePdfBucket(event, formik, "/Uploads/grievupload/", "closeUpload", fileName, "pdf").then((uploadedPath) => {
                                                    formik.setFieldValue("closeUpload", uploadedPath || "");
                                                });
                                            }}
                                        />
                                        {formik.values.closeUpload && (
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={() => viewImage(formik.values.closeUpload)}
                                            >
                                                <FaEye />
                                            </button>
                                        )}
                                    </div>
                                    {/* <ErrorMessage name="closeUpload" component="div" className="text-danger small mt-1" /> */}
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
                                <button type="submit" className="btn btn-danger">Submit & Close</button>
                            </div>
                        </Form>
                    </FormikProvider>
                </div>
            </div>
        </div>
    );
}

export default function GrievanceReport() {
    const [activeTab, setActiveTab] = useState("all");
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const userId = sessionStorage.getItem("user_id");

    const roleType = userId === "it_admin" ? "Technical" : "Administrative";
    const fetchReport = async () => {
        setLoading(true);
        try {
            const url = config.url.STU_URL + "grievance-report?roletype=" + roleType;
            const res = await CommonAxiosGet(url);
            if (res?.data?.scode === "01") {
                setRows(res.data.data || []);
            } else {
                setRows([]);
                Swal.fire(res?.data?.sdesc || "Failed to fetch report", "", "error");
            }
        } catch (error) {
            setRows([]);
            Swal.fire("Unable to fetch report", "", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
    }, []);

    const allRows = rows;
    const pendingRows = useMemo(() => rows.filter((row) => row?.status.toLowerCase().includes("pending")), [rows]);
    const closedRows = useMemo(() => rows.filter((row) => row?.status.toLowerCase().includes("closed")), [rows]);

    const visibleRows = useMemo(() => {
        if (activeTab === "pending") return pendingRows;
        if (activeTab === "closed") return closedRows;
        return allRows;
    }, [activeTab, allRows, pendingRows, closedRows]);

    const openEdit = (row) => {
        setSelectedRow(row);
        setIsModalOpen(true);
    };

    const reportColumns = useMemo(() => ([
        {
            id: "slNo",
            header: "Sl.No",
            cell: ({ row }) => row.index + 1,
            meta: { style: { textAlign: "center" } }
        },
        {
            id: "applicant_name",
            header: "Name",
            accessorKey: "applicant_name",
            cell: ({ row }) => row.original.applicant_name,
        },
        {
            id: "candidate_id",
            header: "Id",
            accessorKey: "candidate_id",
            cell: ({ row }) => row.original.candidate_id,
        },
        {
            id: "prblm_type",
            header: "Problem Type",
            accessorKey: "prblm_type",
            cell: ({ row }) => row.original.prblm_type,
        },
        {
            id: "concern",
            header: "Concern",
            accessorKey: "concern",
            cell: ({ row }) => row.original.concern,
        },
        {
            id: "status",
            header: "Status",
            accessorKey: "status_desc",
            cell: ({ row }) => row.original.status || ""
        },
        {
            id: "attachment",
            header: "Attachment",
            cell: ({ row }) => {
                const uploadFile = row.original.grievupload
                return uploadFile ? (
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => viewImage(uploadFile)}
                    >
                        <FaEye />
                    </button>
                ) : "-";
            },
            meta: { style: { textAlign: "center" } }
        },
        {
            id: "submitted_on",
            header: "Submitted On",
            accessorKey: "updated_time",
            cell: ({ row }) => formatDateOnly(row.original.updated_time)
        },
        {
            id: "remarks",
            header: "Remarks",
            accessorKey: "remarks",
            cell: ({ row }) => row.original.status_remarks || "-"
        },
        {
            id: "support_doc",
            header: "Supported Doc",
            cell: ({ row }) => {
                const supportDoc = row.original.support_doc || "";
                return supportDoc ? (
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => viewImage(supportDoc)} > <FaEye /> </button>
                ) : "-";
            },
            meta: { style: { textAlign: "center" } }
        },
        {
            id: "edit",
            header: "Edit / Close Ticket",
            cell: ({ row }) => {
                const status = row?.original?.status || "";
                const isClosed = status.toLowerCase().includes("closed");
                return !isClosed ? (
                    <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => openEdit(row.original)}
                    >
                        <FaPen className="me-1" /> Edit
                    </button>
                ) : <span className="text-danger">{status}</span>;
            },
            meta: { style: { textAlign: "center" } }
        }
    ]), []);

    return (
        <div className="container-fluid py-3">
            <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
                <CommonCardHeader title="Grievance Report" />
            </div>

            <div className="d-flex flex-wrap gap-2 mb-3">
                <button
                    type="button"
                    className={`btn btn-sm ${activeTab === "all" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setActiveTab("all")}
                >
                    All Tickets ({allRows.length})
                </button>
                <button
                    type="button"
                    className={`btn btn-sm ${activeTab === "pending" ? "btn-danger" : "btn-outline-danger"}`}
                    onClick={() => setActiveTab("pending")}
                >
                    Pending Tickets ({pendingRows.length})
                </button>
                <button
                    type="button"
                    className={`btn btn-sm ${activeTab === "closed" ? "btn-success" : "btn-outline-success"}`}
                    onClick={() => setActiveTab("closed")}
                >
                    Closed Tickets ({closedRows.length})
                </button>
            </div>

            <div className="card border-0 shadow-sm">
                <div className="card-body p-0">
                    {loading ? (
                        <div className="p-3 text-muted">Loading report...</div>
                    ) : (
                        <CommonReactTable
                            data={visibleRows}
                            columns={reportColumns}
                            showFooter={false}
                            showPagination={false}
                            showExcel={true}
                            showPrint={true}
                            excelFileName="Grievance_Report"
                        />
                    )}
                </div>
            </div>

            <CloseTicketModal
                isOpen={isModalOpen}
                row={selectedRow || {}}
                onClose={() => { setIsModalOpen(false); setSelectedRow(null); }}
                onSuccess={fetchReport}
            />
        </div>
    );
}
